// riskCalculator.js
//
// 骨粗鬆症リスク（信号色）の判定ロジック。
// 仕様は SPEC.md 「4. リスク判定ロジック」に記載。変更する際は必ず SPEC.md を先に更新すること。
//
// 判定結果は 'blue' / 'yellow' / 'red' の3種類。
// 旧仕様にあった 'redred'（ダブル赤信号）は廃止した。
// 判定に必要な入力が欠けている場合は null を返す（呼び出し側でエラー表示にする）。

// Clinical Risk を構成する6項目（骨折歴は含まない）。SPEC.md 4.1 参照。
const CLINICAL_RISK_FIELDS = [
    'parentHipFracture',      // ステップ5: 親の股関節骨折
    'currentSmoking',         // ステップ6: 現在の喫煙
    'alcohol',                // ステップ7: 毎日の飲酒
    'glucocorticoids',        // ステップ8: ステロイドの飲み薬
    'rheumatoidArthritis',    // ステップ9: 関節リウマチ
    'secondaryOsteoporosis'   // ステップ10: 続発性骨粗鬆症
];

/**
 * Clinical Risk が「陽性」かどうかを判定する。
 * 6項目のうち1つでも「はい」であれば陽性。
 * @param {object} formData
 * @returns {boolean}
 */
function hasClinicalRisk(formData) {
    if (!formData) return false;
    return CLINICAL_RISK_FIELDS.some(field => formData[field] === true);
}

/**
 * 信号色を判定する。
 *
 * @param {object} params
 * @param {number} params.age            年齢（40以上）
 * @param {string} params.gender         'male' または 'female'
 * @param {number} params.osta           OSTA値（(体重-年齢)*0.2）
 * @param {boolean} params.previousFracture 骨折歴の有無
 * @param {boolean} params.clinicalRisk  Clinical Risk 陽性かどうか
 * @returns {('blue'|'yellow'|'red'|null)} 判定できない場合は null
 */
function determineRisk({ age, gender, osta, previousFracture, clinicalRisk }) {
    // --- 判定可能かの検証 ---
    // 入力が欠けている場合に既定値で埋めて判定してはならない。
    // 誤った信号を出すくらいなら判定不能として扱う（SPEC.md 4.7(c)）。
    if (typeof age !== 'number' || !Number.isFinite(age) || age < 40) {
        return null;
    }
    if (gender !== 'male' && gender !== 'female') {
        return null;
    }
    if (typeof osta !== 'number' || !Number.isFinite(osta)) {
        return null;
    }

    // --- 手順1: 骨折歴があれば年齢・性別を問わず赤 ---
    if (previousFracture === true) {
        return 'red';
    }

    // --- 手順2: 骨折歴がない場合 ---
    const O = osta <= -1;        // OSTA≦-1 を満たすか
    const C = clinicalRisk === true;  // Clinical Risk 陽性か

    if (gender === 'female') {
        if (age < 65) {
            // 女性 40〜64歳: 該当数2個で赤、1個で黄、0個で青
            if (O && C) return 'red';
            if (O || C) return 'yellow';
            return 'blue';
        }
        // 女性 65歳以上: どちらか1つでも該当すれば赤、両方非該当なら黄
        return (O || C) ? 'red' : 'yellow';
    }

    // 男性は OSTA が判定結果に影響しない（SPEC.md 4.2 の仕様。バグではない）
    if (age < 75) {
        // 男性 40〜74歳
        return C ? 'yellow' : 'blue';
    }
    // 男性 75歳以上
    return C ? 'red' : 'yellow';
}

module.exports = {
    CLINICAL_RISK_FIELDS,
    hasClinicalRisk,
    determineRisk
};
